import React from 'react';
import { Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import HourlyForecastTimeline from './HourlyForecastTimeline';
import { HourlyForecastTimelineData, ErrorInfo } from '../../models/HourlyForecastTimelineData';
import { NotificationService } from '../../services/NotificationService';
import * as styles from '../../styles/MapTimelineContainerStyles';

interface ExtendedTimelineData extends HourlyForecastTimelineData {
  errors?: ErrorInfo[];
}

interface MapTimelineContainerProps {
  timelineData: ExtendedTimelineData | null;
  airportName: string;
  compact?: boolean;
}

const MapTimelineContainer: React.FC<MapTimelineContainerProps> = ({ timelineData, airportName, compact = false }) => {
  const isCompact = !!compact;  // Ensure it's always boolean
  if (!timelineData) {
    return (
      <Box sx={styles.noDataContainerStyles}>
        <Typography variant="h6" sx={styles.noDataTitleStyles}>
          {airportName}
        </Typography>
        <Typography variant="body2" sx={styles.noDataMessageStyles}>
          Forecast data not available for this location
        </Typography>
      </Box>
    );
  }

  // Check for data issues using the NotificationService (same as timeline view)
  const dataIssue = NotificationService.checkForDataIssues({
    timelineData: [timelineData],
    loading: false,
    hasCustomNow: false,
    error: null
  });

  // Check if we have missing data warnings/errors
  const hasMissingActuals = dataIssue?.id?.includes('MISSING_ACTUALS') || false;
  const hasMissingPredictions = dataIssue?.id?.includes('MISSING_PREDICTIONS') || false;
  const hasExcessiveMissing = dataIssue?.id?.includes('EXCESSIVE_') || false;

  // Determine what type of warning to show
  const hasWarning = (hasMissingActuals || hasMissingPredictions) && !hasExcessiveMissing;
  const hasError = hasExcessiveMissing;

  return (
    <Box sx={styles.getContainerStyles(isCompact)}>
      <Typography
        variant={isCompact ? "subtitle2" : "h6"}
        sx={styles.getTitleStyles(isCompact, hasWarning, hasError)}
      >
        {airportName}
      </Typography>
      {hasWarning && dataIssue && (
        <Box sx={styles.getWarningBoxStyles(isCompact)}>
          <WarningAmberIcon sx={styles.getWarningIconStyles(isCompact)} />
          <Typography
            variant="caption"
            sx={styles.getWarningTextStyles(isCompact)}
          >
            {dataIssue.message.replace(` (${airportName})`, '')}
          </Typography>
        </Box>
      )}
      {hasError && dataIssue && (
        <Box sx={styles.getErrorBoxStyles(isCompact)}>
          <ErrorIcon sx={styles.getErrorIconStyles(isCompact)} />
          <Typography
            variant="caption"
            sx={styles.getErrorTextStyles(isCompact)}
          >
            {dataIssue.message.replace(` (${airportName})`, '')}
          </Typography>
        </Box>
      )}
      <Box sx={styles.getTimelineWrapperStyles(isCompact)}>
        <HourlyForecastTimeline
          timelineData={timelineData}
          useUTCTime={false}
          hideTitle={true}
          hideLegend={true}
          compact={isCompact}
        />
      </Box>
    </Box>
  );
};

export default MapTimelineContainer;